import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Input, Button, Form, message } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { clientLogin } from "../../services/client";
import { unreachable } from "../../util/unreachable";
import * as amplitude from "@amplitude/analytics-browser";

const LoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Mutation for login
  const loginMutation = useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      clientLogin({
        body: {
          grant_type: "client_credentials",
        },
        headers: {
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        throwOnError: true,
      }),
  });

  const onFinish = (values: { username: string; password: string }) => {
    setLoading(true);
    loginMutation.mutate(values, {
      onSuccess: (response) => {
        const { data } = response;

        message.success("Login successful!");
        switch (data.type) {
          case "admin_partner_access":
            localStorage.setItem("token", data.access_token);
            navigate("/");
            break;
          case "partner_access":
            amplitude.setUserId(values.username + "_partner");
            amplitude.setGroup("user_types", "partner");

            localStorage.setItem("token", data.access_token);
            navigate("/");
            break;

          case "site_owner_access":
            amplitude.setUserId(values.username + "_site_owner");
            amplitude.setGroup("user_types", "site_owner");

            localStorage.setItem("token", data.access_token);
            navigate("/");
            break;

          case "admin_access":
            localStorage.setItem("admin-token", data.access_token);
            navigate("/admin");
            break;

          case "driver_access":
          case "admin_driver_access":
            message.error("Driver access is not supported in this portal.");
            setLoading(false);
            break;

          default:
            return unreachable(data.type);
        }
      },
      onError: () => {
        setLoading(false);
        message.error("Invalid username or password!");
      },
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md p-6 bg-white rounded shadow-md">
        <h2 className="text-center text-2xl font-bold mb-4">Login</h2>
        <Form
          name="login"
          initialValues={{ remember: true }}
          onFinish={onFinish}
          layout="vertical"
        >
          {/* Username Field */}
          <Form.Item
            name="username"
            rules={[{ required: true, message: "Please input your username!" }]}
          >
            <Input
              prefix={<UserOutlined className="text-gray-400" />}
              placeholder="Username"
              size="large"
            />
          </Form.Item>

          {/* Password Field */}
          <Form.Item
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-400" />}
              placeholder="Password"
              size="large"
            />
          </Form.Item>

          {/* Submit Button */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              size="large"
              loading={loading}
            >
              Log in
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default LoginPage;
